import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  X,
  Search,
  Loader2,
  CalendarDays,
  MapPin,
  StickyNote,
  Clock,
  UserRound,
  Tag,
  CheckCircle2,
  XCircle,
  UserCheck,
  UserX,
} from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toDatetimeLocal, statusMeta } from "../AppointmentCalendar/appointmentUtils";

const emptyForm = {
  contactId: null,
  scheduledAt: "",
  durationMinutes: 30,
  reasonId: "",
  professionalId: "",
  location: "",
  notes: "",
};

const labelClass =
  "flex items-center gap-1.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2";
const inputClass =
  "w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all";

const AppointmentModal = ({
  open,
  onClose,
  appointmentId,
  initialContact,
  initialScheduledAt,
  onSaved,
}) => {
  const isMounted = useRef(true);
  const [form, setForm] = useState(emptyForm);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [contactOptions, setContactOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("scheduled");
  const [daySlots, setDaySlots] = useState(null); // null = not loaded yet

  useEffect(() => () => { isMounted.current = false; }, []);

  // Free slots for the picked day, so the agent can see the agenda instead of
  // guessing. The datetime input stays free-form: staff may overbook on purpose.
  const slotDate = form.scheduledAt ? form.scheduledAt.slice(0, 10) : "";

  useEffect(() => {
    if (!open || !slotDate) {
      setDaySlots(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("/appointment-slots", {
          params: {
            professionalId: form.professionalId || undefined,
            durationMinutes: Number(form.durationMinutes) || 30,
            fromDate: slotDate,
            days: 1,
            excludeAppointmentId: appointmentId || undefined,
          },
        });
        if (cancelled || !isMounted.current) return;
        setDaySlots(data.hasAvailability ? data.days?.[0]?.slots || [] : null);
      } catch {
        if (!cancelled && isMounted.current) setDaySlots(null);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [open, slotDate, form.professionalId, form.durationMinutes, appointmentId]);

  // Reference data for the dropdowns.
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      try {
        const [reasonsRes, usersRes] = await Promise.all([
          api.get("/appointment-reasons", { params: { onlyActive: true } }),
          api.get("/users", { params: { pageNumber: 1 } }),
        ]);
        if (!isMounted.current) return;
        setReasons(reasonsRes.data || []);
        setUsers(usersRes.data?.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    load();
  }, [open]);

  // Existing appointment, or defaults for a new one.
  useEffect(() => {
    if (!open) return;

    if (!appointmentId) {
      setForm({
        ...emptyForm,
        contactId: initialContact?.id || null,
        scheduledAt: initialScheduledAt || "",
      });
      setSelectedContact(initialContact || null);
      setStatus("scheduled");
      return;
    }

    const load = async () => {
      try {
        const { data } = await api.get(`/appointments/${appointmentId}`);
        if (!isMounted.current) return;
        setForm({
          contactId: data.contactId,
          scheduledAt: toDatetimeLocal(data.scheduledAt),
          durationMinutes: data.durationMinutes || 30,
          reasonId: data.reasonId || "",
          professionalId: data.professionalId || "",
          location: data.location || "",
          notes: data.notes || "",
        });
        setSelectedContact(data.contact || null);
        setStatus(data.status);
      } catch (err) {
        toastError(err);
      }
    };
    load();
  }, [appointmentId, open, initialContact, initialScheduledAt]);

  // Contact search, same debounce as NewTicketModal.
  useEffect(() => {
    if (!open || searchParam.length < 3) {
      setContactOptions([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam } });
        if (isMounted.current) setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted.current) setSearching(false);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchParam, open]);

  const handleClose = () => {
    setForm(emptyForm);
    setSelectedContact(null);
    setSearchParam("");
    setContactOptions([]);
    onClose();
  };

  const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

  // Picking a reason pre-fills its default duration on new appointments.
  const handleReasonChange = (value) => {
    const reason = reasons.find((r) => String(r.id) === String(value));
    setForm((prev) => ({
      ...prev,
      reasonId: value,
      durationMinutes:
        !appointmentId && reason?.defaultDurationMinutes
          ? reason.defaultDurationMinutes
          : prev.durationMinutes,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.contactId) {
      toast.error("Selecciona un contacto para la cita");
      return;
    }
    if (!form.scheduledAt) {
      toast.error("Indica la fecha y hora de la cita");
      return;
    }

    const payload = {
      contactId: form.contactId,
      scheduledAt: new Date(form.scheduledAt).toISOString(),
      durationMinutes: Number(form.durationMinutes) || 30,
      reasonId: form.reasonId ? Number(form.reasonId) : null,
      professionalId: form.professionalId ? Number(form.professionalId) : null,
      location: form.location || null,
      notes: form.notes || null,
    };

    setSaving(true);
    try {
      const { data } = appointmentId
        ? await api.put(`/appointments/${appointmentId}`, payload)
        : await api.post("/appointments", payload);
      toast.success(
        appointmentId ? "Cita actualizada" : "Cita creada correctamente"
      );
      if (onSaved) onSaved(data);
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/appointments/${appointmentId}/status`, {
        status: newStatus,
      });
      setStatus(data.status);
      toast.success(`Cita marcada como ${statusMeta(newStatus).label.toLowerCase()}`);
      if (onSaved) onSaved(data);
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setSaving(false);
    }
  };

  if (!open) return null;

  const meta = statusMeta(status);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-200 dark:border-slate-700 w-full max-w-2xl mx-4 max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-indigo-600" />
            {appointmentId ? "Editar cita" : "Nueva cita"}
            {appointmentId && (
              <span className={`ml-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${meta.badge}`}>
                {meta.label}
              </span>
            )}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-5 py-4 space-y-4 bg-slate-50 dark:bg-slate-800/30">
            {/* Contact */}
            <div>
              <p className={labelClass}>
                <UserRound className="w-3 h-3 text-indigo-600" /> Contacto
              </p>
              {selectedContact ? (
                <div className="flex items-center justify-between px-3 py-2.5 rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-950/30">
                  <span className="text-sm text-indigo-800 dark:text-indigo-200">
                    {selectedContact.name}
                    {selectedContact.number && (
                      <span className="text-xs text-indigo-500 dark:text-indigo-400"> — {selectedContact.number}</span>
                    )}
                  </span>
                  {!appointmentId && (
                    <button
                      type="button"
                      onClick={() => { setSelectedContact(null); setField("contactId", null); }}
                      className="text-xs font-medium text-indigo-600 hover:underline"
                    >
                      Cambiar
                    </button>
                  )}
                </div>
              ) : (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    {searching && (
                      <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 animate-spin" />
                    )}
                    <input
                      type="text"
                      placeholder="Buscar contacto por nombre o número..."
                      value={searchParam}
                      onChange={(e) => setSearchParam(e.target.value)}
                      className={`${inputClass} pl-9 pr-9`}
                    />
                  </div>
                  {contactOptions.length > 0 && (
                    <div className="mt-2 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden max-h-40 overflow-y-auto">
                      {contactOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => {
                            setSelectedContact(option);
                            setField("contactId", option.id);
                            setSearchParam("");
                            setContactOptions([]);
                          }}
                          className="w-full text-left px-3 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                        >
                          {option.name}
                          {option.number && (
                            <span className="text-slate-400 text-xs"> — {option.number}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className={labelClass}>
                  <CalendarDays className="w-3 h-3 text-indigo-600" /> Fecha y hora
                </p>
                <input
                  type="datetime-local"
                  required
                  value={form.scheduledAt}
                  onChange={(e) => setField("scheduledAt", e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <p className={labelClass}>
                  <Clock className="w-3 h-3 text-indigo-600" /> Duración (minutos)
                </p>
                <input
                  type="number"
                  min={5}
                  max={1440}
                  step={5}
                  value={form.durationMinutes}
                  onChange={(e) => setField("durationMinutes", e.target.value)}
                  className={inputClass}
                />
              </div>
              <div>
                <p className={labelClass}>
                  <Tag className="w-3 h-3 text-indigo-600" /> Motivo
                </p>
                <select
                  value={form.reasonId}
                  onChange={(e) => handleReasonChange(e.target.value)}
                  className={inputClass}
                >
                  <option value="">Sin motivo</option>
                  {reasons.map((reason) => (
                    <option key={reason.id} value={reason.id}>{reason.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className={labelClass}>
                  <UserCheck className="w-3 h-3 text-indigo-600" /> Profesional
                </p>
                <select
                  value={form.professionalId}
                  onChange={(e) => setField("professionalId", e.target.value)}
                  className={inputClass}
                >
                  <option value="">Sin asignar</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>{user.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {daySlots !== null && (
              <div>
                <p className={labelClass}>
                  <Clock className="w-3 h-3 text-indigo-600" /> Horarios libres ese día
                </p>
                {daySlots.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    No quedan horarios libres ese día. Puedes agendar igual escribiendo la hora arriba.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {daySlots.map((slot) => {
                      const value = toDatetimeLocal(slot.start);
                      const selected = value === form.scheduledAt;
                      return (
                        <button
                          key={slot.start}
                          type="button"
                          onClick={() => setField("scheduledAt", value)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors ${
                            selected
                              ? "bg-indigo-600 text-white border-indigo-600"
                              : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-indigo-400 hover:text-indigo-600"
                          }`}
                        >
                          {slot.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div>
              <p className={labelClass}>
                <MapPin className="w-3 h-3 text-indigo-600" /> Ubicación
              </p>
              <input
                type="text"
                placeholder="Sucursal, consultorio o dirección"
                value={form.location}
                onChange={(e) => setField("location", e.target.value)}
                className={inputClass}
              />
            </div>

            <div>
              <p className={labelClass}>
                <StickyNote className="w-3 h-3 text-indigo-600" /> Notas internas
              </p>
              <textarea
                rows={3}
                placeholder="Solo visibles para el equipo — nunca se envían al contacto"
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value)}
                className={`${inputClass} resize-none`}
              />
            </div>

            {appointmentId && (
              <div className="pt-1">
                <p className={labelClass}>Cambiar estado</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={saving || status === "confirmed"}
                    onClick={() => handleStatusChange("confirmed")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 disabled:opacity-40 transition-colors"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Confirmar
                  </button>
                  <button
                    type="button"
                    disabled={saving || status === "cancelled"}
                    onClick={() => handleStatusChange("cancelled")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-40 transition-colors"
                  >
                    <XCircle className="w-3.5 h-3.5" /> Cancelar cita
                  </button>
                  <button
                    type="button"
                    disabled={saving || status === "completed"}
                    onClick={() => handleStatusChange("completed")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-200 dark:border-indigo-800 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 disabled:opacity-40 transition-colors"
                  >
                    <UserCheck className="w-3.5 h-3.5" /> Asistió
                  </button>
                  <button
                    type="button"
                    disabled={saving || status === "no_show"}
                    onClick={() => handleStatusChange("no_show")}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-amber-200 dark:border-amber-800 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30 disabled:opacity-40 transition-colors"
                  >
                    <UserX className="w-3.5 h-3.5" /> No asistió
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 dark:border-slate-700">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {appointmentId ? "Guardar cambios" : "Crear cita"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AppointmentModal;
